/**
 * iOS ATT ⇄ Cookiebot glue pre natívnu wrapper appku. V appke na iOS rozhoduje
 * o tracking súhlase natívny ATT dialóg (Apple Guideline 5.1.2) namiesto
 * Cookiebot banneru; Cookiebot ostáva zdrojom pravdy o súhlase (blokovanie
 * skriptov, Consent Mode) — ATT výsledok sa doň programovo zapisuje.
 * Android / web / starý iOS bez ATT: no-op, banner funguje ako doteraz.
 *
 * KANONICKÝ ZDROJ: r-flutter-wrapper/docs/web/app-att-cookiebot.js — verziované
 * spolu s bridge kontraktom (docs/bridge-contract.md). Kópie v eshopoch lokálne
 * neupravovať; zmeny robiť tu a rozkopírovať.
 *
 * Toto je Cookiebot varianta CMP adaptéra (per-CMP dotykové body sú v runbooku
 * eshop-integration.md). Wecoma varianta = app-att-wecoma.ts v rajhracieku.
 *
 * Flow na každom page loade v appke:
 *  1. Na iOS zariadení skryť Cookiebot UI hneď pri evale (nech banner neblikne).
 *     Ak ATT nie je podporované, UI sa vráti.
 *  2. Po APP_READY → `ATT_STATUS`:
 *     - `notSupported` → un-hide; o súhlase rozhoduje Cookiebot banner a
 *       consent-settled sa ohlási až po odpovedi na banner (nech systémový prompt
 *       na notifikácie nepríde cezeň)
 *     - `notDetermined` → `ATT_REQUEST` (natívny dialóg) → zápis do Cookiebotu
 *     - inak → zápis uloženého stavu (pokrýva aj odobratie v iOS Nastaveniach;
 *       toggle v Nastaveniach appku zabije, sync prebehne na ďalšom cold starte)
 *  3. Zápis: fast path — ak Cookiebot už má zaznamenanú odpoveď v rovnakom
 *     stave, nerobí sa nič. Inak `submitCustomConsent(...)` (aj pri denial, nech
 *     je odpoveď zaznamenaná a banner sa nepýta znova).
 *  4. Pokus o zobrazenie dialógu (klik na skrytý widget, `Cookiebot.renew()`)
 *     → `OPEN_APP_SETTINGS`, lebo v appke sa rozhodnutie mení v iOS Nastaveniach.
 *
 * Konfigurácia (`window.__appAttCookiebotConfig`, všetko voliteľné):
 *  - `categories`: ktoré Cookiebot kategórie riadi ATT. Default všetky tri
 *    (`["preferences", "statistics", "marketing"]`) = parita s tým, čo väčšinou
 *    robí pôvodné Median glue. Neriadené kategórie si držia aktuálnu hodnotu.
 *  - `alwaysGranted`: kategórie, ktoré sú v appke vždy udelené. Má zmysel pre
 *    `preferences` (funkčné cookies) — banner je v appke skrytý, takže bez toho
 *    by sa nedali udeliť nikdy. Kategórie uvedené v `categories` majú prednosť.
 *
 *    **Mapovanie ATT → kategórie je právne rozhodnutie klienta.** Tri bežné
 *    varianty: (a) ATT riadi všetky tri = default; (b) ATT riadi len
 *    `statistics`+`marketing` a `preferences` ostanú v appke navždy vypnuté;
 *    (c) ATT riadi `statistics`+`marketing` a `preferences` sú vždy udelené —
 *    `{ categories: ["statistics", "marketing"], alwaysGranted: ["preferences"] }`
 *    (odmietnutie sledovania nezhodí funkčné cookies; toto používa lunys).
 *  - `reload`: `true` = po reálnej zmene súhlasu zavolať `RELOAD`. Default
 *    `false` — nový stav súhlasu si Cookiebot uplatní sám bez reloadu, či už
 *    auto-blockingom (`data-blockingmode="auto"` pri vlastnom script tagu), alebo
 *    cez Google Consent Mode, keď je Cookiebot nasadený z GTM (`implementation=gtm`
 *    — tak to má lunys). Zapnúť, ak eshop gatuje tracking aj server-side, t. j.
 *    číta súhlas v PHP a podľa neho rendruje snippety.
 *
 *    Platí pre OBE cesty: iOS (zápis ATT výsledku) aj banner (Android / starý
 *    iOS) — HTML vzniklo s consent stavom z cookie na začiatku page loadu, takže
 *    odpoveď v banneri sa v ňom prejaví až po prekreslení. Reload sa spustí len
 *    keď sa `CookieConsent` cookie reálne zmenila proti stavu pri renderi, takže
 *    opakované `CookiebotOnAccept` na ďalších page loadoch nerobia slučku.
 *  - `hideSelector`: CSS selektor Cookiebot UI (default dialóg + underlay +
 *    widget), keby si projekt renderoval vlastné.
 *  - `disabled`: `true` = úplný no-op. Pre eshop (alebo shop v multishope), ktorý
 *    CMP nemá vôbec: nie je do čoho súhlas zapísať, takže v appke netrackujeme —
 *    a keď netrackujeme, ATT sa nežiada (Apple povolenie na niečo, čo nerobíme).
 *    Neohlási ani `__appConsentGluePresent`, takže prompt na notifikácie nečaká
 *    na consent, ktorý nikdy nepríde. As-built: lunys B2B (`b2b.lunys.sk` má len
 *    informačnú lištu, nie consent CMP).
 *
 * Vyžaduje načítaný app-bridge.js (`window.AppBridge`) — načítať PO ňom a PRED
 * app-push.js (kvôli `__appConsentGluePresent`). Vite/TS eshopy:
 * `import "<cesta>/app-att-cookiebot.js"` (side-effect, žiadne exporty).
 */
(function () {
    "use strict";

    const bridge = window.AppBridge;

    // mimo appky no-op — o súhlase rozhoduje Cookiebot banner ako doteraz
    if (!bridge || !bridge.isApp) {
        return;
    }

    const config = window.__appAttCookiebotConfig || {};

    // Eshop (alebo shop v multishope) bez CMP: nie je do čoho súhlas zapísať,
    // takže v appke nič netrackujeme — a keď netrackujeme, nemáme dôvod žiadať
    // ATT. Úplný no-op vrátane `__appConsentGluePresent`, aby systémové prompty
    // (notifikácie) nečakali na consent, ktorý nikdy nepríde.
    if (config.disabled === true) {
        return;
    }

    const ALL_CATEGORIES = ["preferences", "statistics", "marketing"];
    const governed = config.categories || ALL_CATEGORIES;
    const alwaysGranted = (config.alwaysGranted || []).filter(function (category) {
        return governed.indexOf(category) === -1;
    });
    const HIDE_CLASS = "app-att-hide-cmp";
    const HIDE_SELECTOR =
        config.hideSelector ||
        "#CybotCookiebotDialog, #CybotCookiebotDialogBodyUnderlay, #CookiebotWidget";
    const READY_TIMEOUT_MS = 10000;

    const warn = function (message, detail) {
        if (window.console) {
            console.warn("[att-cookiebot] " + message, detail === undefined ? "" : detail);
        }
    };

    const sleep = function (ms) {
        return new Promise(function (resolve) {
            setTimeout(resolve, ms);
        });
    };

    const isIosDevice = function () {
        const ua = window.navigator.userAgent;

        return (
            /iPad|iPhone|iPod/.test(ua) || (ua.indexOf("Mac") !== -1 && "ontouchend" in document)
        );
    };

    // Skrývame cez triedu na <html> + CSS pravidlo, nie inline style — pravidlo
    // platí aj keď Cookiebot vloží svoje UI do DOM neskôr (žiadne blikanie).
    // Pre nulové bliknutie pri prvom paint pridaj rovnaký <style> aj server-side
    // (šablóna vie UA skôr než sa načíta Cookiebot script).
    const ensureHideStyle = function () {
        if (document.getElementById("app-att-hide-cmp-style")) {
            return;
        }

        const style = document.createElement("style");

        style.id = "app-att-hide-cmp-style";
        style.textContent =
            "." +
            HIDE_CLASS +
            " " +
            HIDE_SELECTOR.split(",")
                .map(function (selector) {
                    return selector.trim();
                })
                .join(", ." + HIDE_CLASS + " ") +
            " { display: none !important; visibility: hidden !important; pointer-events: none !important; }";
        (document.head || document.documentElement).appendChild(style);
    };

    const setCmpHidden = function (hidden) {
        ensureHideStyle();

        if (hidden) {
            document.documentElement.classList.add(HIDE_CLASS);
        } else {
            document.documentElement.classList.remove(HIDE_CLASS);
        }
    };

    // Kontrakt pre app-push.js: nech si notifikačný prompt počká, kým je consent
    // dorešený (na iOS inak vyskočia dva systémové prompty naraz).
    window.__appConsentGluePresent = true;

    const markConsentSettled = function () {
        if (window.__appConsentSettled === true) {
            return;
        }

        window.__appConsentSettled = true;

        if (typeof window.dispatchEvent !== "function") {
            return;
        }

        try {
            window.dispatchEvent(new window.Event("app-consent-settled"));
        } catch (error) {
            warn("event app-consent-settled sa nepodarilo odoslať:", error);
        }
    };

    // Consent stav tak, ako ho vidí server — rovnaká `CookieConsent` cookie, akú
    // číta PHP pri server-side gatovaní. `none` = cookie nie je, čiže stránka sa
    // vyrenderovala bez súhlasu. Hodnota je URL-encoded a NIE je platný JSON
    // (`{stamp:'…',necessary:true,statistics:false,…}`) → čítame per kategóriu.
    const cookieConsentSignature = function () {
        const raw = typeof document.cookie === "string" ? document.cookie : "";
        const match = raw.match(/(?:^|;\s*)CookieConsent=([^;]*)/);

        if (!match) {
            return "none";
        }

        let value = match[1];

        try {
            value = decodeURIComponent(value);
        } catch (error) {
            // poškodená cookie — ostane v encoded podobe, porovnanie stále funguje
        }

        return ALL_CATEGORIES.map(function (category) {
            return new RegExp(category + ":\\s*true").test(value) ? "1" : "0";
        }).join("");
    };

    // Stav pri renderi HTML — čítame synchronne pri evale, teda skôr než doň môže
    // zasiahnuť banner alebo zápis ATT výsledku.
    const renderedConsent = cookieConsentSignature();
    let reloadRequested = false;

    const cookiebot = function () {
        return window.Cookiebot;
    };

    const isUsable = function () {
        const cb = cookiebot();

        return !!(cb && typeof cb.submitCustomConsent === "function" && cb.consent);
    };

    // Cookiebot script sa načítava async a jeho eventy mohli vyletieť skôr než
    // tento modul — preto poll aj listenery.
    const whenCookiebotReady = function () {
        if (isUsable()) {
            return Promise.resolve(cookiebot());
        }

        return new Promise(function (resolve) {
            const events = ["CookiebotOnConsentReady", "CookiebotOnLoad", "CookiebotOnDialogInit"];
            let timer = null;

            const finish = function (value) {
                if (timer !== null) {
                    clearInterval(timer);
                    timer = null;
                }

                events.forEach(function (event) {
                    window.removeEventListener(event, onEvent);
                });

                resolve(value);
            };

            const onEvent = function () {
                if (isUsable()) {
                    finish(cookiebot());
                }
            };

            events.forEach(function (event) {
                window.addEventListener(event, onEvent);
            });

            let waited = 0;

            timer = setInterval(function () {
                waited += 200;

                if (isUsable()) {
                    finish(cookiebot());
                } else if (waited >= READY_TIMEOUT_MS) {
                    finish(null);
                }
            }, 200);
        });
    };

    // Počká, kým používateľ odpovie na Cookiebot banner (alebo hneď resolvne, ak už
    // odpovedal). Bez timeoutu zámerne: keď na banner neodpovie, consent nie je
    // dorešený a systémové prompty, ktoré naň čakajú, sa nemajú prečo pýtať.
    const whenCookiebotAnswered = function () {
        const answered = function () {
            const cb = cookiebot();

            return !!(cb && cb.hasResponse === true);
        };

        if (answered()) {
            return Promise.resolve();
        }

        return new Promise(function (resolve) {
            const events = ["CookiebotOnConsentReady", "CookiebotOnAccept", "CookiebotOnDecline"];

            const onEvent = function () {
                if (!answered()) {
                    return;
                }

                events.forEach(function (event) {
                    window.removeEventListener(event, onEvent);
                });

                resolve();
            };

            events.forEach(function (event) {
                window.addEventListener(event, onEvent);
            });
        });
    };

    // Prekreslenie po odpovedi v banneri (Android / starý iOS). Reloaduje len keď
    // sa cookie reálne zmenila proti stavu pri renderi — Cookiebot posiela
    // `CookiebotOnAccept` aj na page loade s už udeleným súhlasom, čo by inak bola
    // nekonečná slučka. Vracia `true`, ak sa reload spustil.
    // `true`, ak sa consent v cookie zmenil proti stavu pri renderi → reload.
    const triggerReloadIfChanged = function () {
        if (reloadRequested) {
            return true;
        }

        if (cookieConsentSignature() === renderedConsent) {
            return false;
        }

        reloadRequested = true;

        bridge.callBridge("RELOAD").catch(function () {
            window.location.reload();
        });

        return true;
    };

    // Zaznamenaná odpoveď (`hasResponse`) je súčasť podmienky — bez nej by sme
    // pri ATT denial nikdy nezapísali explicitné „ne" (fresh stav je tiež false).
    const stateMatches = function (granted) {
        const cb = cookiebot();

        if (!cb || cb.hasResponse !== true || !cb.consent) {
            return false;
        }

        return ALL_CATEGORIES.every(function (category) {
            if (governed.indexOf(category) !== -1) {
                return cb.consent[category] === granted;
            }

            // alwaysGranted patrí do porovnania, inak by fast path preskočil
            // ich udelenie na čerstvej inštalácii; ostatné sú „ako sú"
            return alwaysGranted.indexOf(category) === -1 || cb.consent[category] === true;
        });
    };

    // Vracia `true`, ak sa spustil reload — vtedy sa consent NEoznačuje ako
    // dorešený (stránka aj tak zaniká a push prompt by vyskočil do reloadu).
    const applyAttResult = async function (granted) {
        // fast path: stav už sedí (bežný page load) — bez čakania na Cookiebot
        if (stateMatches(granted)) {
            return false;
        }

        const cb = await whenCookiebotReady();

        if (!cb) {
            warn("Cookiebot sa nenačítal — ATT výsledok sa nezapísal");

            return false;
        }

        if (stateMatches(granted)) {
            return false;
        }

        // Aj denial zapisujeme cez submitCustomConsent (nie withdraw) — withdraw
        // odpoveď zmaže, takže by sa banner tváril ako nezodpovedaný a stav by
        // sa prepisoval na každom page loade.
        const args = ALL_CATEGORIES.map(function (category) {
            if (governed.indexOf(category) !== -1) {
                return granted;
            }

            return alwaysGranted.indexOf(category) !== -1 || cb.consent[category] === true;
        });

        cb.submitCustomConsent(args[0], args[1], args[2]);

        if (config.reload !== true) {
            return false;
        }

        // Reload až keď sa zápis prejaví v `CookieConsent` cookie — server číta ju,
        // nie JS stav, takže skorší reload by vyrenderoval starý stav a glue by sa
        // točil v slučke. Keď sa zápis neprejaví, reload sa preskočí: tracking
        // ostane blokovaný (bezpečný smer) do najbližšej navigácie.
        for (let attempt = 0; attempt < 10; attempt++) {
            if (stateMatches(granted) && triggerReloadIfChanged()) {
                return true;
            }

            await sleep(300);
        }

        warn("zápis súhlasu sa neprejavil — reload preskočený");

        return false;
    };

    // Dialóg je v appke skrytý, takže jeho zobrazenie znamená „user chce zmeniť
    // rozhodnutie" (klik na widget / renew()) → iOS Nastavenia. Prvé automatické
    // zobrazenie pri nezodpovedanom banneri ale Nastavenia otvárať nesmie.
    const interceptDialog = function () {
        const openSettings = function () {
            const cb = cookiebot();

            if (cb && typeof cb.hide === "function") {
                cb.hide();
            }

            bridge.callBridge("OPEN_APP_SETTINGS").catch(function (error) {
                warn("OPEN_APP_SETTINGS zlyhalo:", error);
            });
        };

        const cb = cookiebot();

        if (cb && cb.hasResponse === true) {
            window.CookiebotCallback_OnDialogDisplay = openSettings;

            return;
        }

        window.CookiebotCallback_OnDialogDisplay = function () {
            const current = cookiebot();

            if (current && typeof current.hide === "function") {
                current.hide();
            }

            window.CookiebotCallback_OnDialogDisplay = openSettings;
        };
    };

    if (isIosDevice()) {
        // skryť hneď, nech banner neblikne — ak ATT nie je podporované, vráti sa
        setCmpHidden(true);
    }

    bridge
        .whenAppReady()
        .then(function () {
            return bridge.callBridge("ATT_STATUS");
        })
        .then(async function (status) {
            if (status.status === "notSupported") {
                // Android alebo starý iOS — o súhlase rozhoduje Cookiebot banner.
                // Consent-settled sa ohlási až keď na banner reálne odpovie, nech
                // systémový prompt (napr. notifikácie) nepríde cezeň.
                setCmpHidden(false);

                // Keď CMP vôbec nie je, nie je čo odpovedať — consent označíme za
                // dorešený, inak by systémové prompty čakali navždy (`whenCookiebotAnswered`
                // je zámerne bez timeoutu). Deje sa to, keď eshop CMP načítava z GTM
                // a ten je pri server-side gatingu odstrihnutý, keď ho zablokuje
                // adblock, alebo keď eshop CMP nemá vôbec.
                if (!(await whenCookiebotReady())) {
                    warn("Cookiebot sa nenačítal — súhlas sa nedá zaznamenať");
                    markConsentSettled();

                    return;
                }

                if (config.reload !== true) {
                    whenCookiebotAnswered().then(markConsentSettled);

                    return;
                }

                // Server-side gating: každá zmena odpovede (aj neskoršia cez widget)
                // musí prekresliť stránku. Pri reloade sa consent NEoznačuje ako
                // dorešený — stránka aj tak zaniká a push prompt by vyskočil doň.
                //
                // Cookiebot zapíše `CookieConsent` cookie skôr, než pošle event
                // (overené naživo), takže stačí synchrónna kontrola — na page loade
                // s už udelenou odpoveďou tak prompt nič nezdržuje.
                const onBannerAnswer = function () {
                    if (triggerReloadIfChanged()) {
                        return;
                    }

                    markConsentSettled();
                };

                ["CookiebotOnAccept", "CookiebotOnDecline"].forEach(function (event) {
                    window.addEventListener(event, onBannerAnswer);
                });

                whenCookiebotAnswered().then(onBannerAnswer);

                return;
            }

            setCmpHidden(true);
            interceptDialog();

            let reloading;

            if (status.status === "notDetermined") {
                const result = await bridge.callBridge("ATT_REQUEST");

                reloading = await applyAttResult(result.granted === true);
            } else {
                reloading = await applyAttResult(status.granted === true);
            }

            if (!reloading) {
                markConsentSettled();
            }
        })
        .catch(function (error) {
            warn("ATT sync zlyhal:", error);
            markConsentSettled();
        });
})();
